import { SVGIcon } from '@superops/super-icons';
import React, {
  PropsWithChildren,
  createContext,
  useContext,
  useMemo
} from 'react';
import { ReactSortable } from 'react-sortablejs';
import nanoid from 'nanoid';
import { Row } from '@nile/components';
import { arrayMove } from '@dnd-kit/sortable';
import { noop } from 'lodash';

function renderDragHandle(id: string, className?: string) {
  return (
    <div className={`${id} cursor-move hfc ${className} fx`}>
      <SVGIcon name="Reorder" />
    </div>
  );
}

interface ContextType {
  dragHandle: React.ReactNode;
  dragHandleId: string;
}

const SortableContext = createContext<undefined | ContextType>(undefined);

function useSortableContext() {
  const ctx = useContext(SortableContext);

  if (ctx === undefined) {
    console.error(
      'SuperSortable.DragItem/DragHandle should be used inside SuperSortable'
    );
  }

  return ctx;
}

function SortableContextProvider({
  children,
  ...rest
}: PropsWithChildren<ContextType>) {
  return (
    <SortableContext.Provider value={rest}>{children}</SortableContext.Provider>
  );
}

function SuperSortable({
  onDragEnd,
  list,
  children,
  setList,
  ...rest
}: PropsWithChildren<{
  onDragEnd: (arg: Array<any>) => void;
  list: Array<any>;
  setList: (arg: Array<any>) => void;
}>) {
  const dragHandleId = useMemo(() => `super-sortable-${nanoid()}`, []);

  const dragHandle = useMemo(() => renderDragHandle(dragHandleId), [
    dragHandleId
  ]);

  return (
    <SortableContextProvider
      dragHandleId={dragHandleId}
      dragHandle={dragHandle}
    >
      <ReactSortable
        className="sort-wrapper"
        list={list}
        animation={200}
        {...rest}
        handle={`.${dragHandleId}`}
        setList={setList ?? noop}
        onEnd={(e) => {
          if (!list?.length) return;
          const { oldIndex, newIndex } = e;
          onDragEnd(arrayMove(list, oldIndex, newIndex));
        }}
      >
        {typeof children === 'function' ? children({ dragHandle }) : children}
      </ReactSortable>
    </SortableContextProvider>
  );
}

function DragItem({
  gap = 5,
  children,
  dragHandleClassName
}: PropsWithChildren<{
  gap: React.CSSProperties['gap'];
  dragHandleClassName: string;
}>) {
  const context = useSortableContext();

  const dragHandle = context
    ? renderDragHandle(context.dragHandleId, dragHandleClassName)
    : null;

  return (
    <Row gap={gap}>
      {dragHandle}
      {children}
    </Row>
  );
}

function DragHandle({ className }) {
  const context = useSortableContext();

  if (!context) return null;

  return renderDragHandle(context.dragHandleId, className);
}

SuperSortable.DragItem = DragItem;
SuperSortable.DragHandle = DragHandle;

export { SuperSortable };
